import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { FaUser, FaLock, FaSignInAlt, FaExclamationCircle, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';

const Container = styled.div`
  max-width: 550px;
  margin: 3rem auto;
  padding: 2rem;
  background-color: ${props => props.theme.colors.background};
  min-height: calc(100vh - 200px);
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin: 1rem auto;
  }
`;

const LoginCard = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 10px;
  padding: 3rem;
  box-shadow: 0 2px 10px ${props => props.theme.colors.shadow};
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const Title = styled.h1`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  font-size: 2rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ContextMessage = styled.div`
  background-color: ${props => props.theme.colors.warningBg};
  color: ${props => props.theme.colors.warningText};
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.95rem;
  
  svg {
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    padding: 0.75rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  text-align: left;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.text};
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const InputContainer = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 3rem 0.75rem 3rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 5px;
  font-size: 1rem;
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.65rem 2.5rem 0.65rem 2.5rem;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.primary};
`;

const TogglePasswordButton = styled.button`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const LoginButton = styled.button`
  background: linear-gradient(135deg, #8B5A96, #A569BD);
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  margin-bottom: 2rem;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const LinkText = styled.div`
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.95rem;
  margin-top: 1rem;
  
  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const ForgotPasswordLink = styled(Link)`
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  font-size: 0.9rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, user } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Get the redirect reason from location state
  const from = location.state?.from;
  
  const getContextMessage = () => {
    if (from === '/share-hustle') {
      return 'Before you can share your hustle, you need to sign in.';
    } else if (from === '/share-gig') {
      return 'Before you can share your gig, you need to sign in.';
    } else if (from === '/messages') {
      return 'Please sign in to access your messages.';
    } else if (from === '/profile') {
      return 'Please sign in to view your profile.';
    }
    return null;
  };
  
  const contextMessage = getContextMessage();

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate('/find-hustle');
    }
  }, [user, navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    
    try {
      await signIn({
        email: formData.email,
        password: formData.password
      });
      
      toast.success('Login successful! Welcome back.', {
        duration: 3000,
        id: 'login-success' // Prevents duplicate toasts
      });
      navigate('/find-hustle');
    } catch (error) {
      console.error('Login error:', error);
      if (error.message.includes('Invalid login credentials')) {
        toast.error('Invalid email or password');
      } else if (error.message.includes('Email not confirmed')) {
        toast.error('Please verify your email before signing in');
      } else {
        toast.error('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <LoginCard>
        <Title>Welcome Back</Title>
        
        {contextMessage && (
          <ContextMessage>
            <FaExclamationCircle />
            <span>{contextMessage}</span>
          </ContextMessage>
        )}
        
        <form onSubmit={handleLogin}>
          <FormGroup>
            <Label>Email Address</Label>
            <InputContainer>
              <InputIcon>
                <FaUser />
              </InputIcon>
              <Input
                type="email"
                placeholder="your.email@example.com"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </InputContainer>
          </FormGroup>

          <FormGroup>
            <Label>Password</Label>
            <InputContainer>
              <InputIcon>
                <FaLock />
              </InputIcon>
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Your password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
              />
              <TogglePasswordButton 
                type="button" 
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </TogglePasswordButton>
            </InputContainer>
          </FormGroup>

          <LoginButton type="submit" disabled={loading}>
            <FaSignInAlt />
            {loading ? 'Signing In...' : 'Sign In'}
          </LoginButton>
        </form>

        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <ForgotPasswordLink to="#">
            Forgot Password?
          </ForgotPasswordLink>
        </div>

        <LinkText>
          <span>Don't have an account?</span><br/>
          <Link to="/register">Create New Account</Link>
        </LinkText>
        
        <LinkText style={{ marginTop: '1.5rem', fontSize: '0.85rem' }}>
          By signing in, you agree to our{' '}
          <Link to="/terms-and-conditions">Terms & Conditions</Link>
          {' '}and{' '}
          <Link to="/privacy-policy">Privacy Policy</Link>
        </LinkText>
      </LoginCard>
    </Container>
  );
};

export default Login;